<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl px-6 pt-12 pb-14 text-center md:pt-16 md:pb-20">
    <p class="mb-4 eyebrow">{{ 'pages.events.hero.eyebrow' | transloco }}</p>
    <h1
      class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]"
    >
      <span>{{ 'pages.events.hero.titlePrefix' | transloco }}</span>
      <span class="hero-title-accent font-medium italic text-primary">
        {{ 'pages.events.hero.titleAccent' | transloco }}
      </span>
    </h1>
    <p
      class="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]"
    >
      {{ 'pages.events.hero.description' | transloco }}
    </p>
  </header>

  @if (featured(); as event) {
    <section
      #featuredSection
      aria-labelledby="featured-event-title"
      class="scroll-mt-24 grid gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]"
    >
      <div
        class="relative aspect-video w-full overflow-hidden rounded-2xl border border-border bg-ink shadow-elevated"
      >
        @if (featuredVideoUrl(); as videoUrl) {
          <iframe
            [src]="videoUrl"
            [title]="event.title"
            allow="
              accelerometer;
              autoplay;
              clipboard-write;
              encrypted-media;
              gyroscope;
              picture-in-picture;
              web-share;
            "
            allowfullscreen
            class="absolute inset-0 h-full w-full"
          ></iframe>
        } @else {
          <img
            [src]="event.thumbnail"
            [alt]="event.title"
            class="absolute inset-0 h-full w-full object-cover"
          />
        }
      </div>

      <div class="flex flex-col justify-center">
        <p class="eyebrow">{{ 'pages.events.featured' | transloco }} · {{ event.provider }}</p>
        <h2
          id="featured-event-title"
          class="mt-2 font-display text-2xl font-bold leading-[1.15] tracking-[-0.015em] text-foreground md:text-3xl"
        >
          {{ event.title }}
        </h2>
        <p class="mt-3 text-[15px] leading-[1.65] text-muted-foreground">
          {{ event.description }}
        </p>
        <div class="mt-5 flex flex-wrap gap-3">
          <a
            [href]="event.url"
            target="_blank"
            rel="noreferrer"
            class="btn-pill btn-pill--primary group"
          >
            {{ 'pages.events.watch' | transloco }}
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            >
              <path d="M7 7h10v10" />
              <path d="M7 17 17 7" />
            </svg>
          </a>
          <a
            [href]="playlistUrl"
            target="_blank"
            rel="noreferrer"
            class="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground/80 transition hover:border-primary/60 hover:text-primary"
          >
            {{ 'pages.events.openPlaylist' | transloco }}
          </a>
        </div>
      </div>
    </section>
  }

  <section aria-labelledby="events-list-title" class="mt-16">
    <header class="mb-4 flex items-end justify-between">
      <h2 id="events-list-title" class="eyebrow">
        {{ 'pages.events.listTitle' | transloco }}
      </h2>
      <span class="text-xs text-muted-foreground tabular-nums">
        {{ events().length }} {{ 'pages.events.total' | transloco }}
      </span>
    </header>

    <ol class="divide-y divide-foreground/10 border-y border-foreground/10">
      @for (event of events(); track event.url) {
        <li>
          <button
            type="button"
            (click)="selectEvent(event)"
            [attr.aria-current]="isSelected(event) ? 'true' : null"
            [attr.data-selected]="isSelected(event)"
            class="group flex w-full items-center gap-4 px-2 py-4 text-left transition hover:bg-primary/4 focus-visible:bg-primary/4 focus-visible:outline-none data-[selected=true]:bg-primary/6"
          >
            <span
              aria-hidden="true"
              class="relative block aspect-video w-32 shrink-0 overflow-hidden rounded-md border border-border bg-muted sm:w-40"
              [class.border-primary]="isSelected(event)"
            >
              <img
                [src]="event.thumbnail"
                alt=""
                loading="lazy"
                class="absolute inset-0 h-full w-full object-cover transition group-hover:scale-[1.03]"
              />
              <span
                [attr.data-selected]="isSelected(event)"
                class="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/35 data-[selected=true]:bg-primary/40"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="h-5 w-5 text-white drop-shadow"
                >
                  <path d="m8 5 11 7-11 7V5Z" />
                </svg>
              </span>
            </span>

            <span class="min-w-0 flex-1">
              <span class="block eyebrow-muted">
                {{ event.provider }} · {{ formatDate(event.createdAt) }}
              </span>
              <span
                class="mt-1 block font-display text-[15px] font-bold leading-snug tracking-[-0.01em] group-hover:text-secondary"
                [class.text-primary]="isSelected(event)"
                [class.text-foreground]="!isSelected(event)"
              >
                {{ event.title }}
              </span>
            </span>

            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
              [class.text-primary]="isSelected(event)"
            >
              <path d="M7 7h10v10" />
              <path d="M7 17 17 7" />
            </svg>
          </button>
        </li>
      }
    </ol>
  </section>

  <section class="mt-16">
    <div
      class="relative overflow-hidden rounded-3xl bg-gradient-arp px-8 py-12 text-center text-white shadow-elevated md:px-12 md:py-14"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/5 blur-2xl"
      ></div>
      <div class="relative">
        <p class="mb-3 eyebrow-on-dark">{{ 'pages.events.promo.eyebrow' | transloco }}</p>
        <h2 class="font-display text-2xl font-bold leading-tight md:text-3xl">
          <span>{{ 'pages.events.promo.titlePrefix' | transloco }}</span>
          <span class="italic text-primary-glow">
            {{ 'pages.events.promo.titleAccent' | transloco }}
          </span>
        </h2>
        <p class="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/75 md:text-base">
          {{ 'pages.events.promo.description' | transloco }}
        </p>
        <a
          [href]="playlistUrl"
          target="_blank"
          rel="noreferrer"
          class="btn-pill btn-pill--primary mt-7"
        >
          {{ 'pages.events.promo.cta' | transloco }}
        </a>
      </div>
    </div>
  </section>
</app-page-shell>
